import TagUtils from '../../utils/tagUtils';
import ClearButton from '../formItems/clearButton';
import RupTooltip from '../rupTooltip';
import BorderlessTagField from './borderlessTagField';
import Tag from './tag';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { Colors, Icons } from 'copyright-clearance-center-common-ui';
import { forwardRef, useState } from 'react';

export interface IAddTagsInputTag {
	exists?: boolean;
	tag: string;
}

export interface IAddTagsInputProps {
	onInput: (input: string) => void;
	onTagsAppend?: (tags: string[]) => void;
	tags?: IAddTagsInputTag[];
	onTagRemove?: (tag: string) => void;
	searchOnly?: boolean;
	inputValue?: string;
}

const AddTagsInput: React.ForwardRefExoticComponent<IAddTagsInputProps & React.RefAttributes<HTMLInputElement>> =
	forwardRef(({ tags = [], onTagsAppend, inputValue, onInput, onTagRemove, searchOnly }, ref) => {
		const [selected, setSelected] = useState(-1);

		const getPlaceholder = () => {
			if (searchOnly) {
				return localize('Search tags here');
			}

			return localize('Search or add tags here');
		};

		const onContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
			if (inputValue.length > 0) {
				return;
			}

			if (e.key === 'ArrowLeft') {
				if (selected < 0) {
					setSelected(tags.length - 1);
				} else if (selected > 0) {
					setSelected(selected - 1);
				}
			}

			if (e.key === 'ArrowRight' && selected >= 0) {
				setSelected(Math.min(selected + 1, tags.length - 1));
			}

			if (e.key === 'Backspace' && selected < 0) {
				setSelected(tags.length - 1);
			}

			if ((e.key === 'Backspace' || e.key === 'Delete') && selected >= 0 && onTagRemove) {
				onTagRemove(tags[selected].tag);
				setSelected(-1);
			}
		};

		const handleClear = () => {
			onInput('');
			setSelected(-1);
		};

		return (
			<Stack direction="column" spacing={1}>
				<Stack spacing={4} direction="row" data-test-id="test-input" maxWidth="33rem">
					<Stack
						tabIndex={0}
						direction="row"
						border={`1px solid ${Colors.neutral500}`}
						borderRadius={1}
						flexGrow="1"
						paddingX={3}
						paddingY={2}
						paddingRight="2.25rem"
						flexWrap="wrap"
						gap={1}
						overflow="auto"
						maxHeight="4.5rem"
						onKeyDown={onContainerKeyDown}
						position="relative"
					>
						{tags.map((t, i) => (
							<Tag
								tag={t.tag}
								key={t.tag}
								selected={i === selected}
								exists={t.exists}
								onClear={tag => {
									if (onTagRemove) {
										onTagRemove(tag);
									}
								}}
								onClick={() => setSelected(i)}
							/>
						))}
						<BorderlessTagField
							placeholder={tags.length === 0 ? getPlaceholder() : undefined}
							inputValue={inputValue}
							searchOnly={searchOnly}
							onTagsAppend={onTagsAppend}
							clearSelection={() => setSelected(-1)}
							onInput={onInput}
							ref={ref}
						/>
						{inputValue && (
							<div
								style={{
									position: 'absolute',
									right: '0.25rem',
									top: '50%',
									transform: 'translateY(-50%)',
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center'
								}}
							>
								<ClearButton onClick={handleClear} testId="add-tags-clear-input" />
							</div>
						)}
					</Stack>
					<Box hidden={searchOnly} sx={{ fill: Colors.primary500 }} paddingTop={2} minWidth="1rem">
						<RupTooltip
							title={<div dangerouslySetInnerHTML={{ __html: TagUtils.tooltipText }} />}
							sx={{ maxWidth: '20rem' }}
							arrow
						>
							<span dangerouslySetInnerHTML={{ __html: Icons.InfoIcon }} />
						</RupTooltip>
					</Box>
				</Stack>
			</Stack>
		);
	});
AddTagsInput.displayName = 'AddTagsInput';

export default AddTagsInput;
